import {
	Brand,
	BRANDS,
	resolveBrandId
} from "@/config/brands"

// The brand is fixed for the lifetime of the page: it comes from the hostname,
// and a client-side route change cannot change the hostname. Resolving it once
// into a module singleton keeps it out of the Pinia stores, which persist to
// localStorage and would otherwise cache one brand across both domains.
const activeBrand: Brand = BRANDS[resolveBrandId()]

// Returns the brand this page is being served under.
export function useBrand(): Brand {
	return activeBrand
}

// Sets an attribute on an element only when the element is present, so a missing
// tag in index.html degrades quietly instead of throwing during boot.
function setAttributeIfPresent(selector: string, attribute: string, value: string) {
	const element = document.querySelector(selector)

	if (!element) return

	element.setAttribute(attribute, value)
}

// Points the canonical link at this brand's own domain, creating the tag when it
// is missing. public/index.html deliberately ships without one: a hardcoded
// canonical would tell crawlers on the Mission in Motion domain that the content
// really lives on the DB Services domain.
function applyCanonicalUrl(url: string) {
	const existing = document.querySelector('link[rel="canonical"]')

	if (existing) {
		existing.setAttribute("href", url)
		return
	}

	const link = document.createElement("link")

	link.rel = "canonical"
	link.href = url

	document.head.appendChild(link)
}

// Applies the brand to the document shell. The title, favicon and meta
// description are baked in at build time (vue.config.js and public/index.html),
// so they have to be overridden at runtime. Call this before mounting so the
// data-brand palette is in place for the first paint.
//
// This only reaches clients that execute JavaScript. Link unfurlers and most
// crawlers read the served HTML, so they still see the DB Services values.
// Fixing that needs an edge rewrite keyed on the Host header.
export function applyBrand() {
	document.documentElement.dataset.brand = activeBrand.id
	document.title = activeBrand.title

	applyCanonicalUrl(activeBrand.canonicalUrl)

	setAttributeIfPresent('link[rel="icon"]', "href", activeBrand.faviconUrl)
	setAttributeIfPresent('meta[name="description"]', "content", activeBrand.metaDescription)
}
